import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { chatWithCoach, getDailyMotivation, markCoachInsightsRead, getCoachUnreadCount } from '../lib/api';
import { NANOUK_IMAGES } from '../lib/nanouk-assets';
import { CoachQuickPrompts } from '../components/CoachQuickPrompts';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ClearableInput } from '../components/ui/clearable-input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { VoiceInput } from '../components/VoiceInput';
import { CoachHistoryDrawer } from '../components/CoachHistoryDrawer';
import { useLanguage } from '../contexts/LanguageContext';
import { MessageCircle, Send, Sparkles, Lightbulb, User, Loader2, Shield, Brain, Volume2, VolumeX, ImagePlus, X, Paperclip, RotateCcw } from 'lucide-react';
import axios from 'axios';

// Nanouk mascot images. URLs were previously double-concatenated by mistake
// NANOUK_IMAGES now imported from '../lib/nanouk-assets'

export default function Coach() {
    const { t, language } = useLanguage();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [motivation, setMotivation] = useState(null);
    const [images, setImages] = useState([]);
    const [playingTTS, setPlayingTTS] = useState(null);
    const [analysisPeriod, setAnalysisPeriod] = useState('week');
    const [placeholderIdx, setPlaceholderIdx] = useState(0);
    // Tracks the user's active conversation_id so the history drawer can
    // highlight it and the reset button can show the new one on switch.
    const [activeConvId, setActiveConvId] = useState(null);
    const [nanoukWiggle, setNanoukWiggle] = useState(false);
    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const audioRef = useRef(null);

    const API = process.env.REACT_APP_BACKEND_URL;

    // Restore the user's active conversation on mount: load persisted
    // messages from the server (real Nanouk memory across sessions/days).
    // Falls back to the AI-generated welcome that references the last chat.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // 1) Load persisted history first
                const histRes = await axios.get(`${API}/api/coach/messages?limit=80`);
                const persisted = (histRes?.data?.messages || []).map(m => ({
                    role: m.role,
                    content: m.content,
                    // image_thumbnail is a data-URL we render exactly like
                    // freshly-uploaded previews.
                    images: m.image_thumbnail ? [m.image_thumbnail] : undefined,
                    persisted: true,
                    createdAt: m.created_at,
                }));
                if (cancelled) return;
                setActiveConvId(histRes?.data?.conversation_id || null);
                if (persisted.length > 0) {
                    setMessages(persisted);
                } else {
                    // 2) No history yet → contextual welcome from the AI
                    try {
                        const wel = await axios.get(`${API}/api/coach/welcome?language=${language}`);
                        if (cancelled) return;
                        setMessages([{ role: 'assistant', content: wel?.data?.welcome || t('coach_greeting') }]);
                    } catch (_e) {
                        if (cancelled) return;
                        setMessages([{ role: 'assistant', content: t('coach_greeting') }]);
                    }
                }
            } catch (_err) {
                if (cancelled) return;
                // Last-resort fallback so the page never looks broken
                setMessages([{ role: 'assistant', content: t('coach_greeting') }]);
            }
            loadMotivation();
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    // Greet returning Buddy with their unread-insight count, then clear the
    // Coach FAB unread badge. Fire-and-forget — best-effort, never blocks UX.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await getCoachUnreadCount();
                const unread = Number(data?.unread || 0);
                if (unread > 0) {
                    const msg = language === 'de'
                        ? `🐺 Wuff! Ich hab dir ${unread} frische Insight${unread === 1 ? '' : 's'} mitgebracht!`
                        : `🐺 Woof! I brought you ${unread} fresh insight${unread === 1 ? '' : 's'}!`;
                    toast.success(msg, { duration: 5000 });
                    // Charming "happy to see you" wiggle on the header avatar
                    setNanoukWiggle(true);
                    setTimeout(() => setNanoukWiggle(false), 1200);
                }
            } catch { /* ignore */ }
            markCoachInsightsRead().catch(() => { /* silently ignore */ });
        })();
    }, []);

    // Rotating placeholder for dynamic coach input
    const placeholderHints = language === 'de' ? [
        'Wie war meine Woche?',
        'Was soll ich heute essen?',
        'Analysiere meinen Schlaf',
        'Gib mir einen Trainingsplan',
        'Welche Supplements passen zu mir?',
        'Wie kann ich besser schlafen?',
        'Was sagt mein Readiness Score?',
        'Tipps für meine Reise nach...',
        'Lade ein Foto deiner Mahlzeit hoch',
        'Wie steht es um meine Korrelationen?',
    ] : [
        'How was my week?',
        'What should I eat today?',
        'Analyze my sleep',
        'Give me a training plan',
        'Which supplements fit me?',
        'How can I sleep better?',
        'What does my Readiness Score say?',
        'Travel tips for my next trip',
        'Upload a photo of your meal',
        'How are my correlations?',
    ];
    useEffect(() => {
        const id = setInterval(() => {
            setPlaceholderIdx(i => (i + 1) % placeholderHints.length);
        }, 3500);
        return () => clearInterval(id);
    }, [placeholderHints.length]);

    const loadMotivation = async () => {
        try {
            const response = await getDailyMotivation();
            setMotivation(response.data);
        } catch (error) {
            console.error('Failed to load motivation:', error);
        }
    };

    const handleSend = async () => {
        if ((!input.trim() && images.length === 0) || loading) return;

        // Hands-free: if the message came from voice input, auto-read the answer
        const wasVoice = voicePendingRef.current;
        voicePendingRef.current = false;

        const userMessage = input.trim() || (language === 'de' ? 'Analysiere diese Bilder' : 'Analyze these images');
        setInput('');
        
        const msgObj = { role: 'user', content: userMessage };
        if (images.length > 0) msgObj.images = images.map(i => i.preview);
        setMessages(prev => [...prev, msgObj]);
        
        setLoading(true);

        try {
            const payload = { message: userMessage, period: analysisPeriod };
            if (images.length > 0) {
                payload.image_base64 = images[0].base64;
                payload.image_type = images[0].type || 'image/jpeg';
            }
            const response = await axios.post(`${API}/api/coach/chat`, payload, {
                // PDF + GPT-4o analysis can take 30-50s; raise default so we
                // don't hit a silent client-side abort on slow document reads.
                timeout: 90000,
                // Larger inline payload limits for PDF/image uploads.
                maxContentLength: 25 * 1024 * 1024,
                maxBodyLength: 25 * 1024 * 1024,
            });
            // If backend told us the conversation is too long, transparently
            // reset the LLM session so the next message starts fresh.
            if (response.data.error_kind === 'context_too_long') {
                axios.post(`${API}/api/coach/reset-session`, null).catch(() => {});
            }
            setMessages(prev => {
                const next = [...prev, {
                    role: 'assistant',
                    content: response.data.response,
                    suggestions: response.data.suggestions,
                    errorKind: response.data.error_kind || null,
                }];
                // Auto-play the answer after a voice message (hands-free conversation)
                if (wasVoice && response.data.response && !response.data.error_kind) {
                    setTimeout(() => handlePlayTTS(response.data.response, next.length - 1), 200);
                }
                return next;
            });
        } catch (error) {
            // Surface the actual error so issues like 413 (payload too large),
            // 504 (gateway timeout), or network failures are debuggable from
            // the chat itself instead of always showing a generic apology.
            const status = error?.response?.status;
            const serverMsg = error?.response?.data?.detail || error?.response?.data?.response || '';
            const code = error?.code;
            let techHint = '';
            if (code === 'ECONNABORTED') techHint = language === 'de' ? 'Zeitüberschreitung (90s)' : 'Timeout (90s)';
            else if (status) techHint = `HTTP ${status}${serverMsg ? ` – ${String(serverMsg).slice(0, 120)}` : ''}`;
            else if (error?.message) techHint = String(error.message).slice(0, 160);
            const base = language === 'de'
                ? 'Entschuldigung, ich konnte deine Anfrage nicht verarbeiten.'
                : 'Sorry, I could not process your request.';
            toast.error(t('error_loading'));
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: techHint ? `${base} (${techHint})` : base,
            }]);
        } finally {
            setLoading(false);
            setImages([]);
        }
    };

    // Compress large images before base64-encoding so we don't blow past
    // upstream payload limits. CRITICAL: we ALWAYS re-encode images through
    // canvas → JPEG so the MIME type sent to the backend is GUARANTEED
    // `image/jpeg`, even if iOS hands us a HEIC, PNG or weird custom MIME.
    // This makes the backend routing decision deterministic regardless of
    // which deployed version is running.
    const compressImageIfNeeded = (file) => new Promise((resolve) => {
        const MAX_DIM = 1920; // px
        if (!file.type.startsWith('image/')) {
            // Non-image (e.g. PDF) handled by separate path.
            const reader = new FileReader();
            reader.onload = (ev) => resolve({ dataUrl: ev.target.result, type: file.type });
            reader.readAsDataURL(file);
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;
                if (width > MAX_DIM || height > MAX_DIM) {
                    const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                // ALWAYS emit JPEG — overrides PNG/HEIC/any input format.
                const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                resolve({ dataUrl, type: 'image/jpeg' });
            };
            img.onerror = () => resolve({ dataUrl: ev.target.result, type: file.type });
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    });

    // Render each page of a PDF to a JPEG image in the browser so we can send
    // it through the working Vision path on the backend (which doesn't depend
    // on LiteLLM's flaky PDF file_data handling).
    // Reports a `progress` callback (0..1) so the UI can show real progress
    // instead of a frozen toast — important for big multi-page docs.
    const renderPdfToImages = async (file, onProgress) => {
        // Lazy-load pdfjs only when needed to keep initial bundle small.
        const pdfjsLib = await import('pdfjs-dist/build/pdf.mjs');
        // Bundle the worker from the same package so we don't need a CDN URL.
        const workerUrl = (await import('pdfjs-dist/build/pdf.worker.mjs?url')).default;
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const pageCount = Math.min(pdf.numPages, 5); // cap at 5 pages → keeps payload reasonable
        const images = [];
        for (let i = 1; i <= pageCount; i++) {
            const page = await pdf.getPage(i);
            // 2x scale → good readability for OCR while staying under ~2 MB per page
            const viewport = page.getViewport({ scale: 2.0 });
            const canvas = document.createElement('canvas');
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const ctx = canvas.getContext('2d');
            await page.render({ canvasContext: ctx, viewport, canvas }).promise;
            const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
            images.push({ dataUrl, type: 'image/jpeg' });
            if (onProgress) onProgress(i, pageCount);
        }
        return images;
    };

    // Tracks PDF rendering progress so the UI can show a real progress pill
    // instead of a frozen "preparing" toast.
    const [pdfProgress, setPdfProgress] = useState(null); // { current, total } | null

    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const remaining = 5 - images.length;
        if (remaining <= 0) {
            toast.error(language === 'de' ? 'Max 5 Bilder' : 'Max 5 images');
            return;
        }

        const toProcess = files.slice(0, remaining);
        toProcess.forEach(async (file) => {
            if (file.size > 10 * 1024 * 1024) {
                toast.error(`${file.name}: ${language === 'de' ? 'Zu gross (max 10MB)' : 'Too large (max 10MB)'}`);
                return;
            }
            // PDF path: render pages to images and add each as an image attachment.
            if (file.type === 'application/pdf') {
                try {
                    setPdfProgress({ current: 0, total: 1 });
                    toast.info(language === 'de' ? 'PDF wird vorbereitet...' : 'Preparing PDF...');
                    const pages = await renderPdfToImages(file, (current, total) => {
                        setPdfProgress({ current, total });
                    });
                    setPdfProgress(null);
                    setImages(prev => {
                        const room = 5 - prev.length;
                        const toAdd = pages.slice(0, room).map(p => ({
                            preview: p.dataUrl,
                            base64: p.dataUrl.split(',')[1],
                            type: p.type,
                        }));
                        return [...prev, ...toAdd];
                    });
                    toast.success(language === 'de'
                        ? `${pages.length} Seite${pages.length > 1 ? 'n' : ''} bereit für die Analyse`
                        : `${pages.length} page${pages.length > 1 ? 's' : ''} ready for analysis`);
                } catch (err) {
                    setPdfProgress(null);
                    console.error('PDF render failed:', err);
                    toast.error(language === 'de'
                        ? 'PDF konnte nicht gelesen werden. Versuche einen Screenshot.'
                        : 'PDF could not be read. Try a screenshot instead.');
                }
                return;
            }
            // Image path: compress if needed, then attach.
            const { dataUrl, type } = await compressImageIfNeeded(file);
            setImages(prev => [...prev, {
                preview: dataUrl,
                base64: dataUrl.split(',')[1],
                type,
            }]);
        });
        e.target.value = '';
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const handlePlayTTS = async (text, msgIndex) => {
        if (playingTTS === msgIndex) {
            audioRef.current?.pause();
            setPlayingTTS(null);
            return;
        }
        try {
            setPlayingTTS(msgIndex);
            const response = await axios.post(`${API}/api/coach/tts`, { message: text }, {
                responseType: 'blob'
            });
            const audioUrl = URL.createObjectURL(response.data);
            if (audioRef.current) audioRef.current.pause();
            const audio = new Audio(audioUrl);
            audioRef.current = audio;
            audio.onended = () => setPlayingTTS(null);
            audio.play();
        } catch (e) {
            toast.error(language === 'de' ? 'Sprachausgabe fehlgeschlagen' : 'TTS failed');
            setPlayingTTS(null);
        }
    };

    const handleResetChat = async () => {
        try {
            const resetRes = await axios.post(`${API}/api/coach/reset-session`, null);
            setActiveConvId(resetRes?.data?.conversation_id || null);
            // Fetch a fresh contextual welcome — old conversation is archived
            // server-side and still accessible via /conversations.
            try {
                const wel = await axios.get(`${API}/api/coach/welcome?language=${language}`);
                setMessages([{ role: 'assistant', content: wel?.data?.welcome || t('coach_greeting') }]);
            } catch (_e) {
                setMessages([{ role: 'assistant', content: t('coach_greeting') }]);
            }
            toast.success(language === 'de' ? '↻ Neue Konversation' : '↻ New conversation');
        } catch (e) {
            toast.error(language === 'de' ? 'Reset fehlgeschlagen' : 'Reset failed');
        }
    };

    // Called from CoachHistoryDrawer after the user picks an old conversation.
    // The drawer has already activated it server-side; we only swap the UI.
    const handleResumeConversation = (messagesFromConv, convId) => {
        setMessages(messagesFromConv && messagesFromConv.length > 0
            ? messagesFromConv
            : [{ role: 'assistant', content: t('coach_greeting') }]);
        setActiveConvId(convId);
    };

    const handleSuggestionClick = (suggestion) => {
        // Special-case: the reset suggestion triggers a session reset instead of a chat message
        const sLower = (suggestion || '').toLowerCase();
        if (sLower.includes('chat zurücksetzen') || sLower === 'reset chat') {
            handleResetChat();
            return;
        }
        setMessages(prev => [...prev, { role: 'user', content: suggestion }]);
        setLoading(true);
        axios.post(`${API}/api/coach/chat`, { message: suggestion, period: analysisPeriod })
            .then(res => {
                if (res.data.error_kind === 'context_too_long') {
                    axios.post(`${API}/api/coach/reset-session`, null).catch(() => {});
                }
                setMessages(prev => [...prev, { role: 'assistant', content: res.data.response, suggestions: res.data.suggestions, errorKind: res.data.error_kind || null }]);
            })
            .catch(() => toast.error('Fehler'))
            .finally(() => setLoading(false));
    };

    const [interimTranscript, setInterimTranscript] = useState('');
    // True while the pending input text originated from voice (enables hands-free auto-read)
    const voicePendingRef = useRef(false);

    const handleVoiceResult = (transcript) => {
        // Append final transcript to existing input instead of overwriting,
        // so the user can combine typed + spoken portions.
        voicePendingRef.current = true;
        setInput((prev) => {
            const sep = prev && !prev.endsWith(' ') ? ' ' : '';
            return (prev + sep + transcript).trimStart();
        });
        setInterimTranscript('');
    };

    const handleVoiceInterim = (text) => {
        setInterimTranscript(text);
    };

    // Quick-prompt callback used by <CoachQuickPrompts /> on the empty state.
    // Posts the question directly so the user sees the assistant reply in-line.
    const handleAskNanouk = (query) => {
        setMessages((prev) => [...prev, { role: 'user', content: query }]);
        setLoading(true);
        axios
            .post(
                `${API}/api/coach/chat`,
                { message: query, period: analysisPeriod },
                {},
            )
            .then((res) =>
                setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]),
            )
            .catch(() => toast.error(language === 'de' ? 'Fehler' : 'Error'))
            .finally(() => setLoading(false));
    };

    // Note: legacy handleNutritionAdvice / handleFitnessAdvice / handleHealthAdvice
    // were replaced by the unified handleAskNanouk in CoachQuickPrompts. Removed
    // here to avoid drift between two parallel code paths.

    const quickQuestions = [
        t('improve_sleep'),
        t('what_to_eat'),
        t('workout_tip'),
        t('stay_fit_traveling')
    ];

    return (
        <div className="p-4 lg:p-8 pb-0 lg:pb-8 h-[calc(100vh-4rem)] lg:h-screen flex flex-col" data-testid="coach-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto flex-1 flex flex-col min-h-0 w-full"
            >
                {/* Compact header */}
                <div className="mb-2 flex items-center gap-3 shrink-0">
                    <img
                        src={NANOUK_IMAGES.happy}
                        alt="Nanouk"
                        className={`w-10 h-10 rounded-xl object-cover shadow ${nanoukWiggle ? 'animate-nanouk-wiggle' : ''}`}
                        data-testid="coach-header-nanouk"
                    />
                    <div className="flex-1 min-w-0">
                        <h1 className="font-display text-lg font-bold">{t('coach_title')}</h1>
                        <p className="text-xs text-muted-foreground truncate">{t('coach_subtitle')}</p>
                    </div>
                    <CoachHistoryDrawer activeId={activeConvId} onResume={handleResumeConversation} />
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleResetChat}
                        title={language === 'de' ? 'Neue Konversation' : 'New conversation'}
                        aria-label={language === 'de' ? 'Neue Konversation' : 'New conversation'}
                        data-testid="coach-reset-btn"
                        className="text-muted-foreground hover:text-foreground"
                    >
                        <RotateCcw className="w-5 h-5" />
                    </Button>
                </div>

                {/* Show empty-state prompts when there are no messages yet, OR
                    when the only message is the auto-loaded welcome bubble.
                    Without the second case the buttons would never render in
                    normal flow because handleResetChat always seeds a welcome. */}
                {(messages.length === 0
                    || (messages.length === 1 && messages[0]?.role === 'assistant')) && (
                    <CoachQuickPrompts
                        language={language}
                        motivation={motivation}
                        onAskNanouk={handleAskNanouk}
                    />
                )}

                {/* Chat Area - takes all remaining space */}
                <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                        <div className="space-y-4">
                            {messages.map((msg, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${
                                        msg.role === 'user' 
                                            ? 'bg-primary text-primary-foreground' 
                                            : ''
                                    }`}>
                                        {msg.role === 'user' ? (
                                            <User className="w-4 h-4" />
                                        ) : (
                                            <img 
                                                src={msg.isNutritionAdvice ? NANOUK_IMAGES.happy : NANOUK_IMAGES.icon} 
                                                alt="Nanouk" 
                                                className="w-full h-full object-cover"
                                            />
                                        )}
                                    </div>
                                    <div className={`max-w-[80%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                                        {msg.images && msg.images.length > 0 && (
                                            <div className="flex gap-1 flex-wrap mb-2">
                                                {msg.images.map((img, idx) => (
                                                    <img key={idx} src={img} alt="Upload" className="h-16 rounded-lg border border-border" />
                                                ))}
                                            </div>
                                        )}
                                        {msg.image && !msg.images && (
                                            <img src={msg.image} alt="Upload" className="max-w-[200px] rounded-xl mb-2 border border-border" />
                                        )}
                                        <div className={`inline-block p-3 rounded-2xl ${
                                            msg.role === 'user'
                                                ? 'bg-primary text-primary-foreground rounded-br-sm'
                                                : 'bg-muted border border-border rounded-bl-sm'
                                        }`}>
                                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                        </div>
                                        {msg.role === 'assistant' && msg.content && (
                                            <button
                                                onClick={() => handlePlayTTS(msg.content, i)}
                                                className="mt-1 p-1.5 rounded-full hover:bg-muted transition-colors inline-flex items-center gap-1"
                                                data-testid={`tts-btn-${i}`}
                                            >
                                                {playingTTS === i ? (
                                                    <VolumeX className="w-3.5 h-3.5 text-primary" />
                                                ) : (
                                                    <Volume2 className="w-3.5 h-3.5 text-muted-foreground" />
                                                )}
                                                <span className="text-[10px] text-muted-foreground">{playingTTS === i ? 'Stop' : (language === 'de' ? 'Vorlesen' : 'Listen')}</span>
                                            </button>
                                        )}
                                        {msg.suggestions && msg.suggestions.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {msg.suggestions.map((suggestion, j) => (
                                                    <Button
                                                        key={`${suggestion}-${j}`}
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-xs rounded-full"
                                                        onClick={() => handleSuggestionClick(suggestion)}
                                                    >
                                                        {suggestion}
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                            {loading && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full overflow-hidden">
                                        <img src={NANOUK_IMAGES.icon} alt="Nanouk" className="w-full h-full object-cover" />
                                    </div>
                                    <div className="bg-secondary p-3 rounded-2xl">
                                        <div className="flex gap-1">
                                            <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                            <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                            <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </ScrollArea>

                    {messages.length <= 1 && (
                        <div className="p-3 border-t">
                            <div className="flex flex-wrap gap-1.5">
                                {quickQuestions.map((q) => (
                                    <Button key={q} variant="outline" size="sm" className="text-xs rounded-full h-7" onClick={() => handleSuggestionClick(q)}>
                                        {q}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="p-3 border-t bg-card" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.5rem)' }}>
                        {/* Period Selector */}
                        <div className="flex gap-1 mb-2 overflow-x-auto scrollbar-hide">
                            {[
                                { key: 'week', label: '7T' },
                                { key: 'month', label: '30T' },
                                { key: 'quarter', label: '3M' },
                                { key: 'ytd', label: 'YTD' },
                                { key: 'year', label: '1J' },
                            ].map(p => (
                                <button
                                    key={p.key}
                                    onClick={() => setAnalysisPeriod(p.key)}
                                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all shrink-0 ${
                                        analysisPeriod === p.key
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                            <span className="text-[9px] text-muted-foreground self-center ml-1">{language === 'de' ? 'Analyse' : 'Analysis'}</span>
                        </div>
                        {/* PDF rendering progress (only visible while pdfjs is converting pages) */}
                        {pdfProgress && (
                            <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-xs" data-testid="pdf-progress">
                                <Loader2 className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300 animate-spin flex-shrink-0" />
                                <span className="text-amber-900 dark:text-amber-200 truncate">
                                    {language === 'de'
                                        ? `PDF wird gerendert… Seite ${pdfProgress.current}/${pdfProgress.total}`
                                        : `Rendering PDF… page ${pdfProgress.current}/${pdfProgress.total}`}
                                </span>
                            </div>
                        )}
                        {/* Image Previews */}
                        {images.length > 0 && (
                            <div className="mb-2 flex gap-2 flex-wrap">
                                {images.map((img, idx) => (
                                    <div key={idx} className="relative">
                                        <img src={img.preview} alt="Preview" className="h-14 rounded-lg border border-border" />
                                        <button
                                            onClick={() => removeImage(idx)}
                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                                <span className="text-[10px] text-muted-foreground self-end">{images.length}/5</span>
                            </div>
                        )}
                        {/* Live voice transcription preview */}
                        {interimTranscript && (
                            <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-full bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 text-xs" data-testid="voice-interim">
                                <span className="inline-flex h-2 w-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" aria-hidden />
                                <span className="text-red-900 dark:text-red-200 italic truncate">{interimTranscript}</span>
                            </div>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2 items-center">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*,.pdf"
                                multiple
                                onChange={handleImageUpload}
                                className="hidden"
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10 shrink-0 rounded-full"
                                onClick={() => fileInputRef.current?.click()}
                                data-testid="coach-upload-btn"
                            >
                                <ImagePlus className="w-5 h-5 text-muted-foreground" />
                            </Button>
                            <ClearableInput
                                value={input}
                                onChange={(e) => { voicePendingRef.current = false; setInput(e.target.value); }}
                                placeholder={placeholderHints[placeholderIdx]}
                                disabled={loading}
                                className="h-11 bg-muted border-2 border-border text-base transition-all"
                                data-testid="coach-input"
                                clearTestId="coach-input-clear"
                            />
                            <VoiceInput onResult={handleVoiceResult} />
                            <Button
                                type="submit"
                                disabled={(!input.trim() && images.length === 0) || loading}
                                className="rounded-full h-11 w-11 bg-primary text-primary-foreground shrink-0"
                                data-testid="coach-send-btn"
                            >
                                <Send className="w-5 h-5" />
                            </Button>
                        </form>
                    </div>
                </Card>
            </motion.div>
        </div>
    );
}
